import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  Divider,
  IconButton,
  Typography,
  makeStyles,
} from '@material-ui/core';
import Clear from '@material-ui/icons/Clear';
import OpenInNew from '@material-ui/icons/OpenInNew';

/**
 * Preview one post without leaving the report.
 *
 * Clicking a post used to open a new tab, which threw away the analysis context
 * the user had built up — theme, creator, brand. This keeps them on the page and
 * shows that context alongside the post, with an explicit escape hatch out to
 * the platform.
 *
 * YouTube is embedded directly. Instagram is shown as its thumbnail plus the
 * captured caption: the embed endpoint is unreliable inside an authenticated
 * app, and a broken frame is worse than an honest still.
 */

const useStyles = makeStyles((theme) => ({
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(1.5),
  },
  media: {
    position: 'relative',
    width: '100%',
    background: '#000',
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden',
    marginBottom: theme.spacing(2),
  },
  frame: { display: 'block', width: '100%', aspectRatio: '16 / 9', border: 0 },
  thumb: { display: 'block', width: '100%', maxHeight: 420, objectFit: 'contain' },
  noMedia: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: 200,
    color: theme.palette.text.disabled,
  },
  meta: { display: 'flex', flexWrap: 'wrap', gap: theme.spacing(1), marginBottom: theme.spacing(1.5) },
  stats: { display: 'flex', gap: theme.spacing(3), marginTop: theme.spacing(1.5) },
  statValue: { fontWeight: 700, fontSize: 18, lineHeight: 1.2 },
  statLabel: { fontSize: 11, color: theme.palette.text.secondary, textTransform: 'uppercase' },
  caption: { whiteSpace: 'pre-wrap', lineHeight: 1.55 },
}));

/** The canonical URL for a post, or null when we cannot build one. */
export function postUrl(post, isYouTube) {
  if (!post) return null;
  if (isYouTube) {
    const id = post.video_id || post.id;
    return id ? `https://www.youtube.com/watch?v=${id}` : null;
  }
  return post.shortcode ? `https://www.instagram.com/p/${post.shortcode}` : null;
}

function PostPreviewModal({
  post,
  isYouTube,
  onClose,
  creatorName,
  themeLabel,
  formatNumber,
  formatPercentage,
}) {
  const classes = useStyles();
  if (!post) return null;

  const url = postUrl(post, isYouTube);
  const videoId = isYouTube ? post.video_id || post.id : null;

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogContent>
        <Box className={classes.header}>
          <Box style={{ minWidth: 0 }}>
            <Typography variant="subtitle1" style={{ fontWeight: 700 }}>
              {creatorName || 'Post'}
            </Typography>
            {post.brand && (
              <Typography variant="caption" color="textSecondary">
                {post.brand}
              </Typography>
            )}
          </Box>
          <IconButton size="small" onClick={onClose} aria-label="Close preview">
            <Clear fontSize="small" />
          </IconButton>
        </Box>

        <Box className={classes.media}>
          {isYouTube && videoId ? (
            <iframe
              className={classes.frame}
              src={`https://www.youtube.com/embed/${videoId}`}
              title={post.caption_snippet || 'Post preview'}
              allow="accelerometer; clipboard-write; encrypted-media; picture-in-picture"
              allowFullScreen
            />
          ) : post.thumbnail_url ? (
            <img className={classes.thumb} src={post.thumbnail_url} alt={post.caption_snippet || 'Post'} />
          ) : (
            <Box className={classes.noMedia}>
              <Typography variant="body2">No preview image available</Typography>
            </Box>
          )}
        </Box>

        <Box className={classes.meta}>
          {themeLabel && <Chip size="small" label={themeLabel} />}
          {post.theme_label && post.theme_label !== themeLabel && (
            <Chip size="small" label={post.theme_label} />
          )}
          {post.post_type && <Chip size="small" variant="outlined" label={post.post_type} />}
        </Box>

        <Typography variant="body2" className={classes.caption}>
          {post.caption_snippet || 'No caption captured for this post.'}
        </Typography>

        <Box className={classes.stats}>
          <Box>
            <Typography className={classes.statValue}>
              {formatNumber ? formatNumber(post.view_count || 0) : post.view_count || 0}
            </Typography>
            <Typography className={classes.statLabel}>Views</Typography>
          </Box>
          <Box>
            <Typography className={classes.statValue}>
              {formatPercentage
                ? formatPercentage(post.engagement_rate || 0)
                : `${post.engagement_rate || 0}%`}
            </Typography>
            <Typography className={classes.statLabel}>Engagement</Typography>
          </Box>
        </Box>

        <Divider style={{ margin: '16px 0' }} />

        <Box style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button size="small" onClick={onClose}>
            Close
          </Button>
          <Button
            size="small"
            variant="contained"
            color="primary"
            endIcon={<OpenInNew />}
            disabled={!url}
            onClick={() => url && window.open(url, '_blank', 'noopener,noreferrer')}
          >
            {isYouTube ? 'Open on YouTube' : 'Open on Instagram'}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
}

export default PostPreviewModal;
